import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { User, Crosshair, Wrench, Building2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * <MapaTracking> — mapa REAL de tracking de campo: Leaflet + OpenStreetMap.
 *
 * Decisión de la tanda 2026-07-02 (ver DISENO_MAPA_UX.md): Leaflet + tiles de
 * OSM = US$0, sin API key ni facturación (Google Maps quedó descartado por
 * costo). Los pines son `L.circleMarker` (sin assets de imagen, color semántico
 * directo). Si mañana el tráfico de tiles crece, se migra a un proveedor
 * (MapTiler/Carto) cambiando SOLO `TILE_URL`.
 *
 * Contrato estable con el panel: `pins`, `focusedId`, `onSelect` (no cambió con
 * el placeholder). DEGRADACIÓN: sin pines con coordenadas no se pinta mapa (solo
 * la lista de abajo) — la vista nunca se rompe por falta de ubicación.
 */

export interface MapPinData {
  id: string;
  kind: 'technician' | 'target' | 'job' | 'center';
  label: string;
  sublabel?: string;
  /** Detalle extra del popup (p. ej. registro del técnico al cerrar); se trunca. */
  detail?: string;
  lat: number | null;
  lng: number | null;
  /** Color semántico del pin (estado del trabajo / zona del técnico). */
  tone?: 'ok' | 'warn' | 'muted' | 'info' | 'amber' | 'brand';
  /** Pin "vivo": palpita (CSS puro) — técnicos con ubicación reciente. */
  pulse?: boolean;
  /** Radio (m) a dibujar alrededor del pin — centros de operación. */
  radiusM?: number;
}

/** Zoom al enfocar un pin desde la lista/tarjeta (más cerca que el encuadre). */
const FOCUS_ZOOM = 16;

const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
/** Centro por defecto (Santo Domingo) cuando hay mapa pero un solo pin. */
const DEFAULT_ZOOM = 12;

const TONE_COLOR: Record<NonNullable<MapPinData['tone']>, string> = {
  ok: '#10b981', // verde: llegó / dentro de zona
  warn: '#ef4444', // rojo: vencido / fuera de zona
  muted: '#64748b', // gris: completado / sin estado
  info: '#3b82f6', // azul: en camino
  amber: '#f59e0b', // ámbar: no completado
  brand: '#7c3aed', // violeta: centros de operación (bases)
};

const toneDot: Record<NonNullable<MapPinData['tone']>, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-red-500',
  muted: 'bg-slate-400',
  info: 'bg-blue-500',
  amber: 'bg-amber-500',
  brand: 'bg-violet-500',
};

const KIND_ICON = { technician: User, target: Crosshair, job: Wrench, center: Building2 } as const;

function popupHtml(pin: MapPinData): string {
  const sub = pin.sublabel ? `<br/><span style="color:#64748b">${escapeHtml(pin.sublabel)}</span>` : '';
  // Texto largo (registro del técnico) truncado: el popup debe seguir legible;
  // el texto completo vive en la tarjeta del trabajo.
  const shortDetail =
    pin.detail && pin.detail.length > 120 ? `${pin.detail.slice(0, 117)}…` : pin.detail;
  const det = shortDetail
    ? `<br/><span style="color:#334155;font-size:11px">${escapeHtml(shortDetail)}</span>`
    : '';
  return `<strong>${escapeHtml(pin.label)}</strong>${sub}${det}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

export function MapaTracking({
  pins,
  focusedId,
  onSelect,
  route,
}: {
  pins: MapPinData[];
  focusedId?: string | null;
  onSelect?: (id: string) => void;
  /** Ruta del día del técnico enfocado (puntos de ponche en orden) — polyline. */
  route?: { lat: number; lng: number }[];
}) {
  const located = pins.filter((p) => p.lat != null && p.lng != null);
  const hasLocated = located.length > 0;
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const markerByIdRef = useRef<Map<string, L.CircleMarker>>(new Map());
  const routeLayerRef = useRef<L.Polyline | null>(null);
  const fittedKeyRef = useRef<string>('');

  // Init del mapa (una vez, cuando hay div — es decir, cuando hay pines ubicados).
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: true, attributionControl: true });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = null;
      fittedKeyRef.current = '';
    };
    // El div solo existe cuando hay pines ubicados; re-evaluar al aparecer.
  }, [hasLocated]);

  // Sincroniza los marcadores con los pines (y ajusta encuadre cuando cambia el set).
  useEffect(() => {
    const map = mapRef.current;
    const layer = markersRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markerByIdRef.current.clear();
    for (const p of located) {
      const isTech = p.kind === 'technician';
      const marker = L.circleMarker([p.lat!, p.lng!], {
        radius: isTech ? 9 : 7,
        color: '#ffffff',
        weight: 2,
        fillColor: TONE_COLOR[p.tone ?? 'muted'],
        fillOpacity: p.kind === 'target' ? 0.5 : 0.95,
        // Ubicación viva → el path del SVG palpita con CSS (ver index.css).
        className: p.pulse ? 'pin-vivo' : undefined,
      });
      marker.bindPopup(popupHtml(p));
      if (onSelect) marker.on('click', () => onSelect(p.id));
      // Centros de operación: además del pin, el CÍRCULO del radio de la base
      // (referencia visual de "dentro/fuera"). Va debajo del pin.
      if (p.kind === 'center' && p.radiusM && p.radiusM > 0) {
        L.circle([p.lat!, p.lng!], {
          radius: p.radiusM,
          color: TONE_COLOR.brand,
          weight: 1,
          fillColor: TONE_COLOR.brand,
          fillOpacity: 0.08,
          interactive: false,
        }).addTo(layer);
      }
      marker.addTo(layer);
      markerByIdRef.current.set(p.id, marker);
    }
    // Encuadre: solo cuando cambia el CONJUNTO de pines (no en cada refresh de 30s,
    // para no pelearle el zoom al usuario).
    const key = located.map((p) => p.id).sort().join('|');
    if (key && key !== fittedKeyRef.current) {
      fittedKeyRef.current = key;
      const bounds = L.latLngBounds(located.map((p) => [p.lat!, p.lng!] as [number, number]));
      if (located.length === 1) map.setView(bounds.getCenter(), DEFAULT_ZOOM);
      else map.fitBounds(bounds.pad(0.2));
    }
  }, [located, onSelect]);

  // Foco desde la lista/tarjeta: VUELA al pin (animado), acerca el zoom y abre
  // la burbuja con el nombre — cero clics extra.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusedId) return;
    const pin = located.find((p) => p.id === focusedId);
    if (!pin) return;
    map.flyTo([pin.lat!, pin.lng!], Math.max(map.getZoom(), FOCUS_ZOOM), { duration: 0.8 });
    markerByIdRef.current.get(focusedId)?.openPopup();
  }, [focusedId, located]);

  // Ruta del día del técnico enfocado: polyline azul punteada (se limpia al
  // desenfocar o cambiar de técnico).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    routeLayerRef.current?.remove();
    routeLayerRef.current = null;
    if (route && route.length >= 2) {
      routeLayerRef.current = L.polyline(
        route.map((p) => [p.lat, p.lng] as [number, number]),
        { color: '#3b82f6', weight: 3, dashArray: '6 8', opacity: 0.8 },
      ).addTo(map);
    }
  }, [route]);

  return (
    <div className="overflow-hidden rounded-lg border border-surface-border dark:border-slate-800">
      {hasLocated ? (
        <div ref={mapDivRef} className="h-72 w-full" data-testid="mapa-leaflet" />
      ) : (
        // Degradación: sin coordenadas no hay mapa; la lista de abajo sigue.
        <div className="flex h-24 items-center justify-center bg-slate-50 text-sm text-slate-400 dark:bg-slate-900">
          Sin ubicaciones para mapear todavía — se listan abajo.
        </div>
      )}

      {/* Lista de pines: leyenda del mapa y fallback legible sin coordenadas. */}
      <ul className="divide-y divide-surface-border dark:divide-slate-800">
        {pins.length === 0 && (
          <li className="px-4 py-3 text-sm text-slate-400">Sin pines para mostrar.</li>
        )}
        {pins.map((p) => {
          const Icon = KIND_ICON[p.kind];
          const hasCoords = p.lat != null && p.lng != null;
          return (
            <li
              key={`${p.kind}:${p.id}`}
              onClick={() => onSelect?.(p.id)}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 text-sm',
                onSelect && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50',
                focusedId === p.id && 'bg-brand-50 dark:bg-brand-900/20',
              )}
            >
              <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', toneDot[p.tone ?? 'muted'])} />
              <Icon className="h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-800 dark:text-slate-100">{p.label}</p>
                {p.sublabel && <p className="truncate text-xs text-slate-400">{p.sublabel}</p>}
              </div>
              <span className="shrink-0 text-xs text-slate-400">
                {hasCoords ? `${p.lat!.toFixed(5)}, ${p.lng!.toFixed(5)}` : 'sin ubicación'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
